import { useState, useCallback } from "react";
import { apiService } from "../services/apiClient";
import { apiUrl } from "../services/apiUrl";

export const useComments = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch threaded comments/posts for a topic identifier
  const fetchPosts = useCallback(async (topicIdentifier) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(apiUrl.aipdPosts(topicIdentifier));
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching posts:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch the user list for the @mention/tag-users dropdown
  const fetchUserEmailsByGroup = useCallback(async () => {
    try {
      const response = await apiService.get(apiUrl.aipdUserEmailsByUserGroupId());
      return response.data;
    } catch (err) {
      console.error("Error fetching user emails:", err.response?.data?.message || err.message);
      throw err;
    }
  }, []);

  // Fetch FAQs for a report (optionally scoped to a group)
  const fetchFaqs = useCallback(async (reportId, groupId) => {
    try {
      const response = await apiService.get(apiUrl.aipdGetFaq(reportId, groupId));
      return response.data;
    } catch (err) {
      console.error("Error fetching FAQs:", err.response?.data?.message || err.message);
      throw err;
    }
  }, []);

  // Create a new topic (Post Your Comment)
  const createTopic = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(apiUrl.aipdCreateTopicV2(), payload);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error creating topic:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Reply to an existing topic/comment
  const createComment = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(apiUrl.aipdCreateCommentV2(), payload);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error creating comment:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Edit an existing post or reply
  const updateComment = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(apiUrl.aipdUpdateCommentV2(), payload);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error updating comment:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a comment/reply
  const deleteComment = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(apiUrl.aipdDeleteCommentV2(), payload);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error deleting comment:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    loading,
    error,
    fetchPosts,
    fetchUserEmailsByGroup,
    fetchFaqs,
    createTopic,
    createComment,
    updateComment,
    deleteComment,
    clearError,
  };
};

export default useComments;
